import { ArticleOutlined, DeleteOutline, EditOutlined, ReorderOutlined } from '@mui/icons-material'
import { Button } from '@mui/material'
import { MRT_RowSelectionState } from 'material-react-table'
import React, { FunctionComponent, useMemo, useState } from 'react'

import StackMenu from '../../../../base/components/MyMenu/StackMenu'
import { StackMenuItem } from '../../../../base/components/MyMenu/StackMenuItem'
import { ContainerSkeleton } from '../../../../base/components/skeleton/ContainerSkeleton'
import { FilterTypeMap } from '../../../../base/components/Table/castMrtFilters.ts'
import { genMrtQueryPagination } from '../../../../base/components/Table/genMrtQueryPagination.ts'
import { MrtDynamicTable } from '../../../../base/components/Table/MrtDynamicTable.tsx'
import { MrtTableConfig } from '../../../../base/components/Table/mrtTypes.ts'
import { useMrtQuery } from '../../../../base/components/Table/useMrtQuery.tsx'
import { TipoArticuloProps } from '../../../../interfaces/tipoArticulo.ts'
import { apiTipoArticuloListado } from '../../api/apiTipoArticuloListado'
import { useTipoArticulo } from '../../hook/useTipoArticulo.tsx'
import TipoArticuloActualizarDialog from '../actualizar/TipoArticuloActualizarDialog'
import TipoArticuloRegistroDialog from '../registro/TipoArticuloRegistroDialog'
import { tipoArticuloColumns } from './tipoArticuloColumns.tsx'
import TipoArticuloOrdenarDialog from './TipoArticuloOrdenarDialog.tsx'

interface OwnProps {}

type Props = OwnProps

/**
 * Listado de tipos de articulo
 * @constructor
 */
const filterTypes: FilterTypeMap<TipoArticuloProps> = {
  codigo: 'string',
  descripcion: 'string',
  activo: 'boolean',
  orden: 'number',
}
const TipoArticuloListado: FunctionComponent<Props> = () => {
  const { action, onEliminar, onActionReset, onActionActualizar, onActionRegistrar, onActionOrdenar } =
    useTipoArticulo()
  const columns = useMemo(() => tipoArticuloColumns, [])
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

  const { refetch, isLoading, ...others } = useMrtQuery({
    queryKey: ['tipo-articulo-listado'],
    queryFn: async (ctx) => {
      const pgs = genMrtQueryPagination(ctx, {
        filterFields: [],
        filterTypes,
      })
      return apiTipoArticuloListado(pgs)
    },
    isServerSide: true,
    queryOptions: {
      refetchOnWindowFocus: false,
      refetchInterval: false,
    },
  })

  const config: MrtTableConfig<TipoArticuloProps> = {
    id: 'tipo-articulo-listado',
    columns,
    showIconRefetch: true,
    showAudit: true,
    rowIconsActions: [
      {
        label: 'Eliminar',
        icon: <DeleteOutline />,
        color: 'error',
        onClick: ({ row, refetch }) => onEliminar(row, () => refetch()),
      },
      {
        label: 'Editar',
        icon: <EditOutlined />,
        onClick: ({ row }) => onActionActualizar(row),
      },
    ],
  }

  return (
    <>
      <StackMenu>
        <StackMenuItem>
          <Button
            variant="contained"
            onClick={onActionOrdenar}
            startIcon={<ReorderOutlined />}
            color={'info'}
          >
            Reordenar categorias
          </Button>
        </StackMenuItem>
        <StackMenuItem>
          <Button
            variant="contained"
            onClick={onActionRegistrar}
            startIcon={<ArticleOutlined />}
            color={'primary'}
          >
            Nuevo tipo articulo
          </Button>
        </StackMenuItem>
      </StackMenu>

      <ContainerSkeleton isLoading={isLoading} type="table" rows={5}>
        <MrtDynamicTable
          config={config}
          {...others}
          state={{ ...others.state, rowSelection }}
          onStateChange={{
            ...others.onStateChange,
            onRowSelectionChange: setRowSelection,
          }}
          refetch={refetch}
        />
      </ContainerSkeleton>

      {action.mode === 'registrar' && (
        <TipoArticuloRegistroDialog
          id="tipoArticuloRegistroDialog"
          keepMounted={false}
          open={action.open}
          onClose={async (value?: TipoArticuloProps) => {
            if (value) {
              await refetch()
            }
            onActionReset()
          }}
        />
      )}

      {action.mode === 'actualizar' && (
        <TipoArticuloActualizarDialog
          id="tipoArticuloActualizarDialog"
          keepMounted={false}
          open={action.open}
          tipoArticulo={action.data}
          onClose={async (resp?: TipoArticuloProps) => {
            if (resp) {
              await refetch()
            }
            onActionReset()
          }}
        />
      )}

      {action.mode === 'ordenar' && (
        <TipoArticuloOrdenarDialog
          id="tipoArticuloOrdenarDialog"
          keepMounted={false}
          open={action.open}
          onClose={async (resp?: boolean) => {
            if (resp) {
              await refetch()
            }
            onActionReset()
          }}
        />
      )}
    </>
  )
}

export default TipoArticuloListado
